import { Plus, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

export const INPUT_TYPES = [
  { value: "number", label: "Number" },
  { value: "text", label: "Text" },
  { value: "number_list", label: "List of numbers" },
  { value: "text_list", label: "List of text" },
];

export const describeInputs = (inputs = []) =>
  inputs.length ? inputs.map((i) => `${i.name}: ${INPUT_TYPES.find((t) => t.value === i.type)?.label || i.type}`).join(", ") : "No inputs";

// Bebras-style input schema editor for Blocks questions: each row is a named, typed input variable
export function BlocksInputsEditor({ inputs = [], onChange, compact = false, testId = "blocks-inputs-editor" }) {
  const update = (idx, patch) => onChange(inputs.map((inp, i) => (i === idx ? { ...inp, ...patch } : inp)));
  const remove = (idx) => onChange(inputs.filter((_, i) => i !== idx));
  const add = () => onChange([...inputs, { name: `input${inputs.length + 1}`, type: "number" }]);

  return (
    <div className="space-y-2" data-testid={testId}>
      {inputs.map((inp, idx) => {
        const isList = inp.type.endsWith("_list");
        return (
          <div key={idx} className="flex flex-wrap items-center gap-2" data-testid={`${testId}-row-${idx}`}>
            <Input
              value={inp.name}
              onChange={(e) => update(idx, { name: e.target.value.replace(/\W/g, "_") })}
              placeholder="variable name"
              className={`font-mono ${compact ? "h-8 text-xs w-32" : "w-40"}`}
              data-testid={`${testId}-name-${idx}`}
            />
            <Select value={inp.type} onValueChange={(type) => update(idx, { type, separator: type.endsWith("_list") ? inp.separator || "space" : undefined, same_line: type.endsWith("_list") ? undefined : inp.same_line })}>
              <SelectTrigger className={compact ? "h-8 text-xs w-36" : "w-44"} data-testid={`${testId}-type-${idx}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {INPUT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {isList ? (
              <Select value={inp.separator || "space"} onValueChange={(separator) => update(idx, { separator })}>
                <SelectTrigger className={compact ? "h-8 text-xs w-40" : "w-44"} data-testid={`${testId}-separator-${idx}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="space">Items on one line</SelectItem>
                  <SelectItem value="line">One item per line</SelectItem>
                </SelectContent>
              </Select>
            ) : idx > 0 && !inputs[idx - 1].type.endsWith("_list") ? (
              <label className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                <input type="checkbox" checked={!!inp.same_line} onChange={(e) => update(idx, { same_line: e.target.checked })} data-testid={`${testId}-sameline-${idx}`} />
                same line as previous
              </label>
            ) : null}
            <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => remove(idx)} data-testid={`${testId}-remove-${idx}`}>
              <Trash2 className="w-3.5 h-3.5 text-destructive" />
            </Button>
          </div>
        );
      })}
      <Button type="button" variant="outline" size="sm" className="gap-1.5 h-8" onClick={add} data-testid={`${testId}-add`}>
        <Plus className="w-3.5 h-3.5" /> Add input
      </Button>
      {inputs.length === 0 && <p className="text-xs text-muted-foreground">No inputs - the program just prints an answer.</p>}
    </div>
  );
}
